import React, { useEffect, useState } from "react";
import Navbar from "../components/Navbar";
import api, { formatApiErrorDetail } from "../api/client";
import { TrendingUp, TrendingDown, BarChart3, Film as FilmIcon, Radar, Trophy, ShieldAlert } from "lucide-react";
import { toast } from "sonner";

const TABS = [
    { k: "index", label: "SSE-FRI Index", Icon: BarChart3 },
    { k: "comps", label: "Comparable Deals", Icon: FilmIcon },
    { k: "revenue", label: "Revenue Predict", Icon: TrendingUp },
    { k: "piracy", label: "Piracy Monitor", Icon: ShieldAlert },
    { k: "festival", label: "Festival Predict", Icon: Trophy },
];

function Sparkline({ series }) {
    if (!series?.length) return null;
    const w = 800, h = 140;
    const vals = series.map(s => s.value);
    const min = Math.min(...vals), max = Math.max(...vals);
    const range = max - min || 1;
    const pts = series.map((s, i) => `${(i / (series.length - 1)) * w},${h - ((s.value - min) / range) * (h - 20) - 10}`).join(" ");
    return (
        <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-40">
            <polyline fill="none" stroke="#e7e2d9" strokeWidth="2" points={pts} />
            <polyline fill="url(#fill)" stroke="none" points={`0,${h} ${pts} ${w},${h}`} opacity="0.15" />
            <defs>
                <linearGradient id="fill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#e7e2d9" stopOpacity="0.8" />
                    <stop offset="100%" stopColor="#e7e2d9" stopOpacity="0" />
                </linearGradient>
            </defs>
        </svg>
    );
}

export default function IntelHub() {
    const [tab, setTab] = useState("index");
    const [films, setFilms] = useState([]);
    const [filmId, setFilmId] = useState("");
    const [idx, setIdx] = useState(null);
    const [comps, setComps] = useState([]);
    const [rev, setRev] = useState(null);
    const [piracy, setPiracy] = useState(null);
    const [fest, setFest] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        (async () => {
            const [a, b, c] = await Promise.all([
                api.get("/intel/sse-fri"),
                api.get("/intel/comparable-deals?limit=25"),
                api.get("/films"),
            ]);
            setIdx(a.data); setComps(b.data.deals || []); setFilms(c.data);
            if (c.data[0]) setFilmId(c.data[0].id);
        })();
    }, []);

    const runRev = async () => {
        setLoading(true); setRev(null);
        try {
            const { data } = await api.post("/intel/revenue-predict", { film_id: filmId });
            setRev(data);
        } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
        finally { setLoading(false); }
    };
    const runPiracy = async () => {
        setLoading(true);
        try { const { data } = await api.get(`/intel/piracy/${filmId}`); setPiracy(data); }
        catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
        finally { setLoading(false); }
    };
    const runFest = async () => {
        setLoading(true); setFest(null);
        try {
            const { data } = await api.post("/intel/festival-predict", { film_id: filmId });
            setFest(data);
        } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
        finally { setLoading(false); }
    };

    return (
        <div className="min-h-screen bg-ink-300 text-silver-100">
            <Navbar />
            <section className="max-w-[1400px] mx-auto px-4 md:px-10 pt-12 pb-24">
                <div className="text-[11px] tracking-[0.3em] uppercase text-platinum mb-3 flex items-center gap-2">
                    <Radar className="w-4 h-4" /> Market Intelligence
                </div>
                <h1 className="font-display text-5xl md:text-6xl leading-none mb-3">The <span className="silver-text italic">data</span> moat.</h1>
                <p className="text-silver-400 mb-10 max-w-2xl">A real-time index of film copyright valuation, comparable deals, AI-driven revenue forecasts, piracy signal, and festival predictions.</p>

                <div className="mb-6 flex items-center gap-3 flex-wrap">
                    <span className="text-[11px] tracking-widest uppercase text-silver-500">Active film:</span>
                    <select value={filmId} onChange={(e) => setFilmId(e.target.value)}
                        className="bg-ink-200 border border-white/10 px-3 py-2 text-sm rounded-sm"
                        data-testid="intel-film-picker">
                        {films.map((f) => <option key={f.id} value={f.id}>{f.title}</option>)}
                    </select>
                </div>

                <div className="flex items-center gap-6 border-b border-white/5 mb-8 overflow-x-auto">
                    {TABS.map(({ k, label, Icon }) => (
                        <button key={k} onClick={() => setTab(k)}
                            className={`flex items-center gap-2 py-4 border-b-2 whitespace-nowrap text-xs uppercase tracking-widest ${
                                tab === k ? "border-platinum text-platinum" : "border-transparent text-silver-500 hover:text-white"
                            }`} data-testid={`intel-tab-${k}`}>
                            <Icon className="w-4 h-4" /> {label}
                        </button>
                    ))}
                </div>

                {tab === "index" && idx && (
                    <div data-testid="fri-panel">
                        <div className="flex flex-wrap items-end gap-8 mb-8">
                            <div>
                                <div className="text-[10px] tracking-widest uppercase text-silver-500 mb-1">SSE Film Rights Index</div>
                                <div className="font-display text-6xl silver-text tabular-nums">{idx.index.toLocaleString()}</div>
                                <div className={`text-sm mt-1 tabular-nums flex items-center gap-1 ${idx.change_pct >= 0 ? "text-emerald-300" : "text-red-300"}`}>
                                    {idx.change_pct >= 0 ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
                                    {idx.change_pct >= 0 ? "+" : ""}{idx.change_pct}% vs prev
                                </div>
                            </div>
                            <div className="text-silver-400 text-sm">
                                {idx.closed_deals_total} deals closed · {idx.films_approved} films approved<br />
                                as of {new Date(idx.as_of).toLocaleString()}
                            </div>
                        </div>
                        <div className="bg-ink-200/40 border border-white/5 rounded-sm p-6 mb-10">
                            <Sparkline series={idx.series_30d} />
                            <div className="text-[10px] tracking-widest uppercase text-silver-500 mt-2">30-day trend</div>
                        </div>
                        <div className="text-[10px] tracking-widest uppercase text-silver-500 mb-3">Sub-indices by genre</div>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                            {idx.by_genre.map((g) => (
                                <div key={g.genre} className="border border-white/10 rounded-sm p-4 bg-ink-200/40">
                                    <div className="text-silver-200 text-sm">{g.genre}</div>
                                    <div className="flex items-center justify-between mt-1">
                                        <div className="font-display text-2xl silver-text tabular-nums">{g.value}</div>
                                        <div className={`text-xs tabular-nums ${g.change_pct >= 0 ? "text-emerald-300" : "text-red-300"}`}>
                                            {g.change_pct >= 0 ? "+" : ""}{g.change_pct}%
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {tab === "comps" && (
                    <div data-testid="comps-panel">
                        <div className="text-[10px] tracking-widest uppercase text-silver-500 mb-3">Closed comparable deals</div>
                        {comps.length === 0 ? (
                            <div className="text-silver-500 text-sm">No closed deals yet — your database grows with every transfer.</div>
                        ) : (
                            <table className="w-full text-sm" data-testid="comps-table">
                                <thead className="text-left text-[10px] tracking-widest uppercase text-silver-500 border-b border-white/10">
                                    <tr><th className="p-3">Title</th><th className="p-3">Country</th><th className="p-3">Genres</th><th className="p-3 text-right">Closed USD</th></tr>
                                </thead>
                                <tbody>
                                    {comps.map((c) => (
                                        <tr key={c.deal_id} className="border-b border-white/5">
                                            <td className="p-3 text-silver-200">{c.title}</td>
                                            <td className="p-3 text-silver-400">{c.country}</td>
                                            <td className="p-3 text-silver-400 text-xs">{(c.genres || []).join(", ")}</td>
                                            <td className="p-3 text-right text-platinum font-mono">${Number(c.closed_usd).toLocaleString()}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>
                )}

                {tab === "revenue" && (
                    <div data-testid="revenue-panel">
                        <button onClick={runRev} disabled={loading} className="bg-platinum hover:bg-white text-ink-400 px-5 py-3 text-xs tracking-widest uppercase rounded-sm mb-6 disabled:opacity-50" data-testid="revenue-run-btn">
                            {loading ? "Modelling…" : "Run 10-year forecast"}
                        </button>
                        {rev && (
                            <div>
                                <div className="mb-6">
                                    <div className="text-[10px] tracking-widest uppercase text-silver-500">Total 10-year projected revenue · {rev.confidence_pct}% conf</div>
                                    <div className="font-display text-6xl silver-text tabular-nums">${Number(rev.total_10y_usd).toLocaleString()}</div>
                                </div>
                                <div className="grid md:grid-cols-2 gap-3 mb-6">
                                    {rev.channels.map((c, i) => (
                                        <div key={i} className="border border-white/10 rounded-sm p-4 bg-ink-200/40">
                                            <div className="flex items-center justify-between">
                                                <div className="text-silver-200">{c.name}</div>
                                                <div className="font-mono text-platinum tabular-nums">${Number(c.y10_total_usd).toLocaleString()}</div>
                                            </div>
                                            <div className="text-[11px] text-silver-500 mt-1">Y1: ${Number(c.y1_usd).toLocaleString()} · {c.note}</div>
                                        </div>
                                    ))}
                                </div>
                                <div className="text-[10px] tracking-widest uppercase text-silver-500 mb-2">Key assumptions</div>
                                <ul className="text-silver-300 text-sm space-y-1">
                                    {(rev.key_assumptions || []).map((k, i) => <li key={i}>◆ {k}</li>)}
                                </ul>
                            </div>
                        )}
                    </div>
                )}

                {tab === "piracy" && (
                    <div data-testid="piracy-panel">
                        <button onClick={runPiracy} disabled={loading} className="bg-platinum hover:bg-white text-ink-400 px-5 py-3 text-xs tracking-widest uppercase rounded-sm mb-6 disabled:opacity-50" data-testid="piracy-run-btn">
                            {loading ? "Scanning…" : "Scan piracy signal"}
                        </button>
                        {piracy && (
                            <div>
                                <div className="mb-6 flex gap-10 flex-wrap">
                                    <div>
                                        <div className="text-[10px] tracking-widest uppercase text-silver-500">Detections · 7 days</div>
                                        <div className="font-display text-5xl silver-text tabular-nums">{piracy.total_detections_7d.toLocaleString()}</div>
                                    </div>
                                    <div>
                                        <div className="text-[10px] tracking-widest uppercase text-silver-500">Est. lost revenue</div>
                                        <div className="font-display text-5xl text-red-300 tabular-nums">${Math.round(piracy.est_lost_revenue_usd).toLocaleString()}</div>
                                    </div>
                                </div>
                                <div className="space-y-2">
                                    {piracy.hits.map((h, i) => (
                                        <div key={i} className="flex items-center justify-between p-3 border border-white/10 rounded-sm bg-ink-200/40">
                                            <div>
                                                <div className="text-silver-200 font-mono text-sm">{h.domain}</div>
                                                <div className="text-[11px] text-silver-500">{h.region}</div>
                                            </div>
                                            <div className="text-right">
                                                <div className="text-silver-300 text-sm tabular-nums">{h.detections_7d.toLocaleString()}</div>
                                                <div className={`text-[10px] tracking-widest uppercase ${h.takedown_status === "resolved" ? "text-emerald-300" : h.takedown_status === "served" ? "text-amber-300" : "text-red-300"}`}>{h.takedown_status}</div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                                <div className="text-[10px] tracking-widest uppercase text-silver-500 mt-4">MOCKED signal · integrate Muso/MarkScan</div>
                            </div>
                        )}
                    </div>
                )}

                {tab === "festival" && (
                    <div data-testid="festival-panel">
                        <button onClick={runFest} disabled={loading} className="bg-platinum hover:bg-white text-ink-400 px-5 py-3 text-xs tracking-widest uppercase rounded-sm mb-6 disabled:opacity-50" data-testid="festival-run-btn">
                            {loading ? "Predicting…" : "Run festival prediction"}
                        </button>
                        {fest && (
                            <div>
                                <div className="border border-platinum/30 bg-platinum/5 rounded-sm p-5 mb-6">
                                    <div className="text-[10px] tracking-widest uppercase text-silver-500 mb-1">Recommended path</div>
                                    <div className="text-silver-100 text-sm">{fest.recommended_path}</div>
                                </div>
                                <div className="space-y-2">
                                    {fest.predictions.map((p, i) => (
                                        <div key={i} className="flex items-center gap-4 p-3 border border-white/10 rounded-sm bg-ink-200/40">
                                            <div className="w-48 text-silver-200">{p.festival}</div>
                                            <div className="flex-1 h-2 bg-white/5 rounded-full overflow-hidden">
                                                <div className="h-full bg-platinum" style={{ width: `${p.prob_pct}%` }} />
                                            </div>
                                            <div className="w-14 text-right text-platinum tabular-nums">{p.prob_pct}%</div>
                                            <div className="hidden md:block text-[11px] text-silver-500 flex-1">{p.rationale}</div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </section>
        </div>
    );
}
